/**
 * 门户（微信登录）本地态管理
 *
 * 安全说明：
 *  - 这里**只**保存 JWT token（`portal:wx_token`）。JWT 由后端签名，客户端即使
 *    篡改 payload，后端解签失败会直接 401，无法伪造身份，因此放 localStorage 是
 *    可接受的（业界通行做法之一）。
 *  - **不再**在 localStorage / sessionStorage / cookie 里持久化用户 profile
 *    （name / organization 等），避免用户在浏览器控制台直接改写字段来冒充身份
 *    做前端逻辑分支（例如把 organization 改成"上海管局"以骗过前端的多选开关）。
 *    真实 profile 由 React Context (`PortalAccountProvider`) 在内存中持有，
 *    刷新页面时会重新拉 `/portal/me` 由后端下发。
 *  - PortalAccount 类型定义保留在这里，作为跨模块共享的接口契约。
 *
 * 使用范围：仅客户端门户（`/portal/*`）；后台管理走太湖票据，不读这里。
 */

const TOKEN_KEY = 'portal:wx_token';

export interface PortalAccount {
  id: number;
  name: string | null;
  nickname?: string | null;
  organization: string | null;
  phone?: string | null;
}

export const getPortalToken = (): string => {
  try {
    return localStorage.getItem(TOKEN_KEY) || '';
  } catch {
    return '';
  }
};

export const setPortalToken = (token: string): void => {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* 忽略 storage 异常（隐私模式） */
  }
};

/**
 * 清空门户登录态。
 *
 * 注意：这里只负责清 token；用户 profile 存在内存 Context 中，
 * 调用方需要在拿到导航跳转前额外调用 `setAccount(null)`（或让页面卸载后
 * Provider 随之刷新）来抹掉 UI 上的显示。
 */
export const clearPortalAuth = (): void => {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* 忽略 */
  }
};

/** 判断当前页是否门户页（用于 401/403 跳转决策） */
export const isPortalPage = (): boolean =>
  typeof window !== 'undefined' && window.location.pathname.startsWith('/portal');
